import "./style.scss";

import { Modal, Tooltip } from "@mui/material";
import { DownloadOutline } from "assets/img/svg/downloadOutline";
import Plus from "assets/img/svg/plus";
import type { TImportDataDictionary } from "components/ImportData/type";
import LocationsManagement from "components/shared/StockFilter/LocationsManagement";
import StyledButton from "components/shared/StyledButton";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { useAbility } from "hooks/useAbility";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TPeriod } from "store/periodStore";
import { PeriodStatus } from "store/types";

import type { ModalPropType } from "..";
import AdvanceProvisionModal from "../AdvanceProvisionModal";
import SubscriptionManagementModalWrapper from "../SubscriptionManagementModal/SubscriptionManagementModalWrapper";

/** Custom export entries always use a string key for i18n lookup. */
type CustomExportMenuItem = Pick<ThreeDotMenuOptions, "id" | "action"> & { name: string };

interface TabButtons {
    title: string;
    /**
     * On which tab the buttons will appear.
     */
    tabType: "general" | "beneficiaries" | "stock";
    /**
     *
     * @param value
     * @returns
     */
    setModalProp: (value: ModalPropType) => void;
    exportGeneralFile: () => void;
    exportBeneficiariesFile: () => void;
    exportStockFile: () => void;
    onImport: (value: ModalPropType, importType?: TImportDataDictionary) => void;
    disabled?: boolean;
    periodStatus: PeriodStatus | undefined;
    selectedPeriod: TPeriod | undefined;
    allowsSubscriptionChannels?: boolean;
    exportDAP?: () => void;
    exportAdvanceReductionReport?: () => void;
    exportAllowanceReport?: () => void;
    customExportMenuItems?: CustomExportMenuItem[];
    tooltip?: string;
    companyId?: number;
    isRoyaltiesRecalculating?: boolean;
    canAddSalesInClosedPeriod?: boolean;
}

/**
 * Component for the buttons on the right of the Management tabs.
 */
const TabButtons = ({
    title,
    tabType,
    setModalProp,
    exportGeneralFile,
    exportBeneficiariesFile,
    exportStockFile,
    onImport,
    disabled = false,
    periodStatus,
    selectedPeriod,
    allowsSubscriptionChannels = false,
    exportDAP,
    exportAdvanceReductionReport,
    exportAllowanceReport,
    customExportMenuItems,
    tooltip,
    companyId,
    isRoyaltiesRecalculating = false,
    canAddSalesInClosedPeriod = false,
}: TabButtons) => {
    const [t] = useTranslation();

    const [modalStatus, setModalStatus] = useState(false);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [advanceProvisionModalOpen, setAdvanceProvisionModalOpen] = useState(false);
    const canReadAdvanceProvision = useAbility().can("read", "AdvanceProvision");

    const handleOpeningLocationsModal = () => setModalStatus(true);

    const isPeriodClosed = periodStatus === PeriodStatus.CLOSED;
    const isPeriodFuture = periodStatus === PeriodStatus.FUTURE;
    const isActionDisabled = isPeriodClosed || isPeriodFuture || isRoyaltiesRecalculating;

    const menuOptionsGeneral: ThreeDotMenuOptions[] = [
        {
            id: 1,
            name: t("Management.AddProvisions"),
            icon: <Plus />,
            action: setModalProp,
            disabled: isActionDisabled,
        },
        {
            id: 2,
            name: t("Management.ImportProvisions"),
            icon: <Plus />,
            action: () => onImport("import", "provision"),
            disabled: isActionDisabled,
        },
        {
            id: 3,
            name: t("Management.BulkUpdateBalance"),
            icon: <Plus />,
            action: () => onImport("import", "balance"),
            disabled: isActionDisabled,
        },
        {
            id: 4,
            name: t("Management.SalesDownload"),
            icon: <DownloadOutline width="16" height="16" />,
            action: exportGeneralFile,
        },
        {
            id: 5,
            name: t("Management.DAPExport"),
            icon: <DownloadOutline width="16" height="16" />,
            action: () => exportDAP && exportDAP(),
        },
        {
            id: 6,
            name: t("Management.AdvanceReductionExport"),
            icon: <DownloadOutline width="16" height="16" />,
            action: () => exportAdvanceReductionReport && exportAdvanceReductionReport(),
        },
        {
            id: 7,
            name: t("Management.AllowanceExport"),
            icon: <DownloadOutline width="16" height="16" />,
            action: () => exportAllowanceReport && exportAllowanceReport(),
        },
        canReadAdvanceProvision && {
            id: "advance-provision",
            name: t("AdvanceProvision.Management.MenuLabel"),
            icon: <Plus />,
            action: () => setAdvanceProvisionModalOpen(true),
        },
        allowsSubscriptionChannels && {
            id: 8,
            name: t("Management.ManageSubscription"),
            icon: <Plus />,
            action: () => setSubscriptionModalOpen(true),
        },
        ...(customExportMenuItems ?? []).map((item, index) => ({
            id: 9 + index,
            name: t(`CustomExport.${item.name.trim()}`),
            icon: <DownloadOutline width="16" height="16" />,
            action: () => item.action && item.action(),
        })),
    ].filter(Boolean) as ThreeDotMenuOptions[];

    const menuOptionsBeneficiaries: ThreeDotMenuOptions[] = [
        { id: 1, name: t("Management.SetMinimum"), icon: <Plus />, action: setModalProp, disabled: isActionDisabled },
        {
            id: 2,
            name: t("Management.BeneficiariesDownload"),
            icon: <DownloadOutline width="16" height="16" />,
            action: exportBeneficiariesFile,
        },
    ];

    const menuOptionsStock: ThreeDotMenuOptions[] = [
        {
            id: 1,
            name: t("Stock.ManageLocations"),
            icon: <Plus />,
            action: handleOpeningLocationsModal,
        },
        {
            id: 2,
            name: t("Stock.DownloadStock"),
            icon: <DownloadOutline width="16" height="16" />,
            action: exportStockFile,
        },
    ];

    const showImportButton = useMemo(() => {
        if (isPeriodClosed) return tabType === "general";
        if (tabType === "general") return true;
        if (tabType === "stock") return !isPeriodFuture;
        return false;
    }, [isPeriodClosed, isPeriodFuture, tabType]);

    return (
        <div className="tab-buttons">
            {/* Modal that shows stock locations and redirects to Settings page */}
            <Modal open={modalStatus} onClose={() => setModalStatus(false)}>
                <LocationsManagement onClose={() => setModalStatus(false)} />
            </Modal>

            {advanceProvisionModalOpen && (
                <AdvanceProvisionModal open onClose={() => setAdvanceProvisionModalOpen(false)} />
            )}

            {/* Subscription Management Modal */}
            {selectedPeriod && subscriptionModalOpen && (
                <Modal open={subscriptionModalOpen} onClose={() => setSubscriptionModalOpen(false)}>
                    <SubscriptionManagementModalWrapper
                        onClose={() => setSubscriptionModalOpen(false)}
                        selectedPeriod={selectedPeriod}
                        onOpenImport={() => {
                            setSubscriptionModalOpen(false);
                            onImport("import", "subscription");
                        }}
                    />
                </Modal>
            )}

            {showImportButton && (
                <Tooltip title={tooltip} arrow>
                    <div>
                        <StyledButton
                            className="action-btn"
                            buttonType="primary"
                            onClick={() => onImport("import", tabType === "general" ? "sales" : "stock")}
                            disabled={disabled || (isPeriodClosed && !canAddSalesInClosedPeriod)}
                        >
                            <Plus />
                            {title}
                        </StyledButton>
                    </div>
                </Tooltip>
            )}
            <ThreeDotMenu
                options={
                    tabType === "general"
                        ? menuOptionsGeneral
                        : tabType === "beneficiaries"
                          ? menuOptionsBeneficiaries
                          : menuOptionsStock
                }
            />
        </div>
    );
};

export default TabButtons;
